import { AfterViewInit, Component, forwardRef, Inject, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { PDF_COVER_TEMPLATE } from '../../common/endpoints';
import { TemplateLoaderService } from '../../dashboard-area/template-loader/template-loader.service';
import { AbstractExportContextService } from '../../service/abstract-export-context.service';
import { DownloadService, ExportPdfMode } from '../../service/download.service';
import { UiService } from '../../service/ui.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';

@Component({
    selector: 'report-pdf',
    template: '',
    styles: [`
        ::ng-deep .pdf-cover {
            display: none;
        }

        ::ng-deep .export-pdf .pdf-cover {
            display: block;
        }

        ::ng-deep .export-pdf .pdf-cover-page-break {
            break-before: page;
            page-break-before: always;
        }

        ::ng-deep .report-mode-pdf-cover-page-break {
            break-before: page;
            page-break-before: always;
        }
    `]
})
export class ReportPdfComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() reportTitle: string = "";

    @Input() mode: ExportPdfMode = ExportPdfMode.REMOTE;

    @Input() fileName: string;

    @Input() coverTemplateName: string;

    @Input() periodRef: string;

    constructor(
        @Inject(forwardRef(() => TemplateLoaderService)) private loader: TemplateLoaderService,
        @Inject(forwardRef(() => DownloadService)) private downloadService: DownloadService,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => UiService)) private uiService: UiService,
        @Inject(forwardRef(() => AbstractExportContextService)) private exportService: AbstractExportContextService,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService
    ) { }

    private _releasePrintResizeMode: (() => void) | null = null;

    preTemplate: string = `<div [ngClass]="reportMode ? 'report-mode-pdf-cover' : 'pdf-cover'">`;
    postTemplate: string = `</div><div [ngClass]="reportMode ? 'report-mode-pdf-cover-page-break' : 'pdf-cover-page-break'"></div>`; //page break

    ngOnInit(): void {
        this.downloadService.setExportPdfButtonVisible();
        this.downloadService.setExportPdfMode(this.mode);
        this.exportService.setReportPdfFileName(this.fileName || (this.reportTitle.toLocaleLowerCase()));
        this.exportService.setPdfPeriodRef(this.periodRef);
        const coverOverride = this.getCoverOverride();
        if (coverOverride === '') return; // the report generation supplied an empty cover: no cover and no page break
        if (coverOverride === null && !this.hasCover()) return;

        const periodValue = this.exportService.getPdfPeriodRefValue();
        const placeholders = { "${reportTitle}": this.exportService.resolveExportFileNamePlaceholders(this.reportTitle, periodValue?.start, periodValue?.end) || "" };
        const templateWrapper = { preTemplate: this.preTemplate, postTemplate: this.postTemplate, placeholders: placeholders }
        if (coverOverride !== null) {
            this.loader.loadFromTextWithWrapper(coverOverride, templateWrapper, this.vcRef, true);
        } else if (this.coverTemplateName) {
            this.uiService.getContentByTemplateName(this.coverTemplateName)
                .then(content => this.loader.loadFromTextWithWrapper(content, templateWrapper, this.vcRef, true))
                .catch(() => this.loader.loadFromEndpoint(PDF_COVER_TEMPLATE, this.vcRef, true, null, templateWrapper));
        } else {
            this.loader.loadFromEndpoint(PDF_COVER_TEMPLATE, this.vcRef, true, null, templateWrapper);
        }
    }

    /**
     * Returns the already selected and resolved cover supplied by the report generation (injected by the report renderer before the
     * page starts), or {@code null} if none was supplied. An empty string is a supplied value meaning "no cover".
     */
    private getCoverOverride(): string | null {
        const cover = (window as any).__servitlyReportGeneration?.cover;
        return typeof cover === 'string' ? cover : null;
    }

    private hasCover(): boolean {
        return !!this.coverTemplateName || !!this.reportTitle;
    }

    ngAfterViewInit(): void {
        this._releasePrintResizeMode = this.widgetPrintResizeCoordinator.activate();
    }

    ngOnDestroy(): void {
        this._releasePrintResizeMode?.();
        this._releasePrintResizeMode = null;
        this.downloadService.setExportPdfButtonInvisible();
    }
}